import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';

const SummaryReport = ({ formData, onBack, onRestart }) => {
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [networkStatus, setNetworkStatus] = useState('checking');
  const [isCheckingNetwork, setIsCheckingNetwork] = useState(false);

  // Ensure formData exists and has default values
  const data = formData || {};
  
  // Google Apps Script endpoint for saving assessment data
  const GOOGLE_APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyZjS8RFOf5g-uV2KgGnYyWOAuhbpFYY-fYv06Pd1DaC7WsizO4zj-MCkq035cRZ1JU/exec';
  
  // Check network connectivity and Google Apps Script availability
  const checkNetworkConnectivity = async () => {
    setIsCheckingNetwork(true);
    setNetworkStatus('checking');
    
    try {
      // Test Google Apps Script connectivity
      const testResponse = await fetch(GOOGLE_APPS_SCRIPT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ test: 'connection' }),
        mode: 'no-cors',
      });
      
      // If we get here without an error, the connection is working
      setNetworkStatus('connected');
      console.log('✅ Google Apps Script connection successful');
    } catch (error) {
      console.log('❌ Google Apps Script connection failed:', error);
      setNetworkStatus('disconnected');
    } finally {
      setIsCheckingNetwork(false);
    }
  };

  // Check network status on component mount
  useEffect(() => {
    checkNetworkConnectivity();
  }, []);

  // Save assessment data directly to Google Sheets using a reliable method
  // IMPORTANT: Only user profile information goes to Google Sheets, assessment data stays local
  const saveAssessmentToGoogleSheets = async (assessmentData, userEmail = null) => {
    // Only save minimal user profile data to Google Sheets
    // Assessment answers remain local only
    const dataToSave = {
      dataType: 'User Profile',
      userEmail: userEmail || 'unknown@example.com',
      timestamp: new Date().toISOString(),
      // Note: Assessment data (nationalPriority, statePriority, etc.) is NOT sent to Google Sheets
      // Only user profile information is saved there
    };

    console.log('⚠️ IMPORTANT: Only user profile data being sent to Google Sheets');
    console.log('Assessment data will remain local only');
    console.log('Data being sent to Google Sheets:', dataToSave);

    // Try Google Apps Script first with retry logic
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        console.log(`Attempt ${attempt}: Saving user profile to Google Apps Script...`);
        
        const response = await fetch(GOOGLE_APPS_SCRIPT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSave),
          mode: 'no-cors', // This bypasses CORS issues
        });

        console.log(`Google Apps Script response received (attempt ${attempt}):`, response);
        
        // Since we're using no-cors, we can't read the response, but if we get here, it likely worked
        setSaveStatus({ type: 'success', message: `User profile sent to Google Sheets! Assessment data remains local. (Attempt ${attempt})` });
        setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
        
        console.log('✅ User profile saved successfully to Google Sheets');
        console.log('📝 Assessment data remains local only');
        return true;

      } catch (error) {
        console.error(`Google Apps Script attempt ${attempt} failed:`, error);
        
        if (attempt === 3) {
          // All attempts failed, try fallback methods
          break;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }

    // Fallback to local server
    try {
      console.log('Attempting to save to local server...');
      const localResponse = await fetch('http://localhost:5001/api/save-assessment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assessmentData: dataToSave,
          userEmail: userEmail
        }),
      });

      if (localResponse.ok) {
        const localResult = await localResponse.json();
        console.log('Local server response:', localResult);
        setSaveStatus({ type: 'success', message: 'Assessment data saved to local server!' });
        setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
        return true;
      } else {
        throw new Error(`Local server failed: ${localResponse.status}`);
      }
    } catch (localError) {
      console.error('Local server also failed:', localError);
      
      // Final fallback: save to localStorage
      try {
        localStorage.setItem('assessmentData', JSON.stringify(dataToSave));
        localStorage.setItem('assessmentLastSync', new Date().toISOString());
        setSaveStatus({ type: 'success', message: 'Assessment data saved locally! Will sync when connection is restored.' });
        setTimeout(() => setSaveStatus({ type: '', message: '' }), 8000);
        return true;
      } catch (storageError) {
        console.error('All save methods failed:', storageError);
        setSaveStatus({ type: 'error', message: 'Failed to save data. Please try again.' });
        setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
        return false;
      }
    }
  };
  
  // Add validation and error handling
  if (!formData || typeof formData !== 'object') {
    return (
      <div className="summary-report">
        <div className="error-message">
          <h2>Error: Invalid Form Data</h2>
          <p>The form data is missing or invalid. Please go back and complete the assessment steps.</p>
          <button onClick={onBack} className="btn btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const downloadPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text('Tax Structuring Summary Report', 20, 20);
    
    // Add subtitle
    doc.setFontSize(14);
    doc.text('Your comprehensive tax optimization assessment', 20, 35);
    
    let yPosition = 50;
    
    // Step 1: Alignment
    doc.setFontSize(16);
    doc.text('Step 1: Alignment', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(12);
    doc.text('1.1 Is your business solving a National Priority or National Paranoia?', 20, yPosition);
    yPosition += 7;
    doc.text(`Answer: ${data.nationalPriority === true ? 'Yes' : data.nationalPriority === false ? 'No' : 'Not answered'}`, 30, yPosition);
    yPosition += 10;
    
    if (data.nationalPriority === true) {
      doc.text('Follow-up: Have you claimed the respective national incentive?', 20, yPosition);
      yPosition += 7;
      doc.text(`Answer: ${data.nationalIncentiveClaimed === true ? 'Yes' : data.nationalIncentiveClaimed === false ? 'No' : 'Not answered'}`, 30, yPosition);
      yPosition += 10;
      
      if (data.nationalIncentiveClaimed === false && data.nationalActionSteps) {
        doc.text('Action Steps:', 20, yPosition);
        yPosition += 7;
        const actionSteps = doc.splitTextToSize(data.nationalActionSteps, 160);
        doc.text(actionSteps, 30, yPosition);
        yPosition += (actionSteps.length * 7) + 5;
      }
    }
    
    doc.text('1.2 Is your business solving a State Priority or State Paranoia?', 20, yPosition);
    yPosition += 7;
    doc.text(`Answer: ${data.statePriority === true ? 'Yes' : data.statePriority === false ? 'No' : 'Not answered'}`, 30, yPosition);
    yPosition += 10;
    
    if (data.statePriority === true) {
      doc.text('Follow-up: Have you claimed the respective state incentive?', 20, yPosition);
      yPosition += 7;
      doc.text(`Answer: ${data.stateIncentiveClaimed === true ? 'Yes' : data.stateIncentiveClaimed === false ? 'No' : 'Not answered'}`, 30, yPosition);
      yPosition += 10;
      
      if (data.stateIncentiveClaimed === false && data.stateActionSteps) {
        doc.text('Action Steps:', 20, yPosition);
        yPosition += 7;
        const actionSteps = doc.splitTextToSize(data.stateActionSteps, 160);
        doc.text(actionSteps, 30, yPosition);
        yPosition += (actionSteps.length * 7) + 5;
      }
    }
    
    // Step 2: Constitution Optimization
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(16);
    doc.text('Step 2: Constitution Optimization', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(12);
    doc.text('2.1 Is your business structure optimized for your needs?', 20, yPosition);
    yPosition += 7;
    doc.text(`Answer: ${data.constitutionOptimized === true ? 'Yes' : data.constitutionOptimized === false ? 'No' : 'Not answered'}`, 30, yPosition);
    yPosition += 10;
    
    if (data.constitutionOptimized === false && data.constitutionActionSteps) {
      doc.text('Action Steps:', 20, yPosition);
      yPosition += 7;
      const actionSteps = doc.splitTextToSize(data.constitutionActionSteps, 160);
      doc.text(actionSteps, 30, yPosition);
      yPosition += (actionSteps.length * 7) + 5;
    }
    
    doc.text('2.2 Does your business structure pass the Bulletproof Test?', 20, yPosition);
    yPosition += 7;
    doc.text(`Answer: ${data.bulletproofTest === true ? 'Yes' : data.bulletproofTest === false ? 'No' : 'Not answered'}`, 30, yPosition);
    yPosition += 10;
    
    if (data.bulletproofTest === false && data.bulletproofActionSteps) {
      doc.text('Action Steps:', 20, yPosition);
      yPosition += 7;
      const actionSteps = doc.splitTextToSize(data.bulletproofActionSteps, 160);
      doc.text(actionSteps, 30, yPosition);
      yPosition += (actionSteps.length * 7) + 5;
    }
    
    // Step 3: Tactical Strategies
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(16);
    doc.text('Step 3: Tactical Strategies', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(12);
    doc.text('3.1 Have you implemented tactical tax strategies?', 20, yPosition);
    yPosition += 7;
    doc.text(`Answer: ${data.tacticalStrategies === true ? 'Yes' : data.tacticalStrategies === false ? 'No' : 'Not answered'}`, 30, yPosition);
    yPosition += 10;
    
    if (data.tacticalStrategies === false && data.tacticalActionSteps) {
      doc.text('Action Steps:', 20, yPosition);
      yPosition += 7;
      const actionSteps = doc.splitTextToSize(data.tacticalActionSteps, 160);
      doc.text(actionSteps, 30, yPosition);
      yPosition += (actionSteps.length * 7) + 5;
    }
    
    // Footer
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(10);
    doc.text('---', 20, yPosition);
    yPosition += 7;
    doc.text('This report was generated by the Tax Structuring Platform', 20, yPosition);
    yPosition += 7;
    doc.text('For questions or support, contact dv@divakarv.com', 20, yPosition);
    
    doc.save('tax-structuring-report.pdf');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsSending(true);
    setEmailError('');
    
    try {
      // Check if user has actually completed the assessment
      const hasCompletedAssessment = data.nationalPriority !== null && 
                                   data.statePriority !== null && 
                                   data.constitutionOptimized !== null && 
                                   data.bulletproofTest !== null && 
                                   data.tacticalStrategies !== null;
      
      if (!hasCompletedAssessment) {
        setEmailError('Please complete the tax structuring assessment before sending the report. All steps must be completed.');
        return;
      }
      
      // Validate that we have form data to send
      if (!data || Object.keys(data).length === 0) {
        setEmailError('No form data available to send. Please complete the tax structuring assessment first.');
        return;
      }
      
      // Debug logging
      console.log('Sending email with data:', data);
      console.log('Data structure:', {
        nationalPriority: data.nationalPriority,
        statePriority: data.statePriority,
        constitutionOptimized: data.constitutionOptimized,
        bulletproofTest: data.bulletproofTest,
        tacticalStrategies: data.tacticalStrategies
      });
      
      // First, try to send the email directly
      console.log('Attempting to send email via server...');
      const emailResponse = await fetch('http://localhost:5001/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          formData: data
        }),
      });
      
      if (!emailResponse.ok) {
        throw new Error(`Email server error: ${emailResponse.status}`);
      }
      
      const emailResult = await emailResponse.json();
      
      if (!emailResult.success) {
        throw new Error(emailResult.message || 'Email service failed');
      }
      
      console.log('✅ Email sent successfully:', emailResult);
      
      // Then save assessment data (this will also save to Google Sheets via the server)
      // IMPORTANT: Only user profile information goes to Google Sheets, assessment data stays local
      let saveResult;
      try {
        console.log('Saving assessment data to server...');
        const saveResponse = await fetch('http://localhost:5001/api/save-assessment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            assessmentData: data,
            userEmail: email.trim()
          }),
        });

        if (!saveResponse.ok) {
          throw new Error('Failed to save assessment data');
        }

        saveResult = await saveResponse.json();
        console.log('Assessment save result:', saveResult);
        
        // If server couldn't save to Google Sheets, try direct approach
        // Note: Only user profile data goes to Google Sheets, assessment data remains local
        if (!saveResult.googleSheets) {
          console.log('Server couldn\'t save user profile to Google Sheets, trying direct approach...');
          await saveAssessmentToGoogleSheets(data, email.trim());
        }
      } catch (saveError) {
        console.error('Server save failed, trying direct Google Apps Script:', saveError);
        // Fallback to direct Google Apps Script (user profile only)
        await saveAssessmentToGoogleSheets(data, email.trim());
      }
      
      // Email was successful
      setEmailSent(true);
      setSaveStatus({ type: 'success', message: '✅ Email sent successfully! Assessment data saved.' });
      
      // Clear success messages after delay
      setTimeout(() => {
        setEmailSent(false);
        setShowEmailInput(false);
        setEmail('');
        setSaveStatus({ type: '', message: '' });
      }, 5000);
      
    } catch (error) {
      console.error('Email submission error:', error);
      
      if (error.message.includes('Email server error') || error.message.includes('Email service failed')) {
        setEmailError(`Email service error: ${error.message}. Please check if the server is running.`);
      } else if (error.message.includes('Failed to save')) {
        setEmailError(`Email sent but failed to save data: ${error.message}`);
      } else {
        setEmailError(`Network error: ${error.message}. Please check if the server is running.`);
      }
    } finally {
      setIsSending(false);
    }
  };

  const getAllActionSteps = () => {
    const steps = [];
    
    // Step 1 actions
    if (data.nationalPriority === true && data.nationalIncentiveClaimed === false && data.nationalActionSteps) {
      steps.push({
        step: 'Step 1: National Priority/Paranoia',
        action: data.nationalActionSteps
      });
    }
    
    if (data.statePriority === true && data.stateIncentiveClaimed === false && data.stateActionSteps) {
      steps.push({
        step: 'Step 1: State Priority/Paranoia',
        action: data.stateActionSteps
      });
    }
    
    // Step 2 actions
    if (data.constitutionOptimized === false && data.constitutionActionSteps) {
      steps.push({
        step: 'Step 2: Constitution Optimization',
        action: data.constitutionActionSteps
      });
    }
    
    if (data.bulletproofTest === false && data.bulletproofActionSteps) {
      steps.push({
        step: 'Step 2: Bulletproof Test',
        action: data.bulletproofActionSteps
      });
    }
    
    // Step 3 actions
    if (data.tacticalStrategies === false && data.tacticalActionSteps) {
      steps.push({
        step: 'Step 3: Tactical Strategies',
        action: data.tacticalActionSteps
      });
    }
    
    return steps;
  };

  const actionSteps = getAllActionSteps();

  // Check if assessment is complete
  const isAssessmentComplete = data.nationalPriority !== null && 
                              data.statePriority !== null && 
                              data.constitutionOptimized !== null && 
                              data.bulletproofTest !== null && 
                              data.tacticalStrategies !== null;

  return (
    <div className="summary-container">
      <h2 className="summary-title">Summary Report</h2>
      
      {/* Network Status Indicator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div className={`network-status ${networkStatus}`} style={{
          padding: '10px',
          borderRadius: '5px',
          fontSize: '0.9em',
          flex: 1,
          marginRight: '10px',
          backgroundColor: networkStatus === 'connected' ? '#d4edda' : 
                          networkStatus === 'disconnected' ? '#f8d7da' : '#fff3cd',
          color: networkStatus === 'connected' ? '#155724' : 
                 networkStatus === 'disconnected' ? '#721c24' : '#856404',
          border: `1px solid ${networkStatus === 'connected' ? '#c3e6cb' : 
                             networkStatus === 'disconnected' ? '#f5c6cb' : '#ffeaa7'}`
        }}>
          {networkStatus === 'checking' && '🔍 Checking network connection...'}
          {networkStatus === 'connected' && '✅ Network connected - Google Sheets available'}
          {networkStatus === 'disconnected' && '❌ Network disconnected - Data will be saved locally'}
        </div>
        <button 
          type="button" 
          onClick={checkNetworkConnectivity}
          disabled={isCheckingNetwork}
          style={{
            padding: '8px 12px',
            fontSize: '0.8em',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isCheckingNetwork ? 'not-allowed' : 'pointer',
            opacity: isCheckingNetwork ? 0.6 : 1
          }}
        >
          {isCheckingNetwork ? '🔄 Checking...' : '🔄 Check'}
        </button>
      </div>
      
      {/* Save Status Display */}
      {saveStatus.message && (
        <div className={`save-status ${saveStatus.type}`} style={{
          padding: '12px',
          borderRadius: '5px',
          marginBottom: '20px',
          fontSize: '0.9em',
          backgroundColor: saveStatus.type === 'success' ? '#d4edda' : '#f8d7da',
          color: saveStatus.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${saveStatus.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {saveStatus.message}
          {saveStatus.message.includes('locally') && (
            <button 
              onClick={() => saveAssessmentToGoogleSheets(data, email)}
              style={{
                marginLeft: '10px',
                padding: '4px 8px',
                fontSize: '0.8em',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              🔄 Sync Now
            </button>
          )}
        </div>
      )}
      
      {/* Assessment Completion Status */}
      <div className={`assessment-status ${isAssessmentComplete ? 'complete' : 'incomplete'}`}>
        <div className="status-icon">
          {isAssessmentComplete ? '✅' : '⚠️'}
        </div>
        <div className="status-text">
          <h3>Assessment Status</h3>
          <p>
            {isAssessmentComplete 
              ? 'All assessment steps have been completed. You can now download the report or send it via email.'
              : 'Some assessment steps are incomplete. Please go back and complete all steps before sending the report.'
            }
          </p>
                  <p style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
          <strong>Note:</strong> Assessment data is being saved to Google Sheets. Local server is available as a fallback.
        </p>
        </div>
      </div>
      
      {/* Step 1 Summary */}
      <div className="summary-section">
        <h3 className="summary-section-title">Step 1: Alignment</h3>
        <div className="summary-item">
          <div className="summary-question">1.1 Is your business solving a National Priority or National Paranoia?</div>
          <div className="summary-answer">Answer: {data.nationalPriority === true ? 'Yes' : data.nationalPriority === false ? 'No' : 'Not answered'}</div>
          
          {data.nationalPriority === true && (
            <>
              <div className="summary-question">Follow-up: Have you claimed the respective national incentive?</div>
              <div className="summary-answer">Answer: {data.nationalIncentiveClaimed === true ? 'Yes' : data.nationalIncentiveClaimed === false ? 'No' : 'Not answered'}</div>
            </>
          )}
        </div>
        
        <div className="summary-item">
          <div className="summary-question">1.2 Is your business solving a State Priority or State Paranoia?</div>
          <div className="summary-answer">Answer: {data.statePriority === true ? 'Yes' : data.statePriority === false ? 'No' : 'Not answered'}</div>
          
          {data.statePriority === true && (
            <>
              <div className="summary-question">Follow-up: Have you claimed the respective state incentive?</div>
              <div className="summary-answer">Answer: {data.stateIncentiveClaimed === true ? 'Yes' : data.stateIncentiveClaimed === false ? 'No' : 'Not answered'}</div>
            </>
          )}
        </div>
      </div>
      
      {/* Step 2 Summary */}
      <div className="summary-section">
        <h3 className="summary-section-title">Step 2: Constitution Optimization</h3>
        <div className="summary-item">
          <div className="summary-question">2.1 Is your business structure optimized for your needs?</div>
          <div className="summary-answer">Answer: {data.constitutionOptimized === true ? 'Yes' : data.constitutionOptimized === false ? 'No' : 'Not answered'}</div>
        </div>
        
        <div className="summary-item">
          <div className="summary-question">2.2 Does your business structure pass the Bulletproof Test?</div>
          <div className="summary-answer">Answer: {data.bulletproofTest === true ? 'Yes' : data.bulletproofTest === false ? 'No' : 'Not answered'}</div>
        </div>
      </div>
      
      {/* Step 3 Summary */}
      <div className="summary-section">
        <h3 className="summary-section-title">Step 3: Tactical Strategies</h3>
        <div className="summary-item">
          <div className="summary-question">3.1 Have you implemented tactical tax strategies?</div>
          <div className="summary-answer">Answer: {data.tacticalStrategies === true ? 'Yes' : data.tacticalStrategies === false ? 'No' : 'Not answered'}</div>
        </div>
      </div>
      
      {/* Action Steps Summary */}
      {actionSteps.length > 0 && (
        <div className="summary-section">
          <h3 className="summary-section-title">Action Steps Summary</h3>
          {actionSteps.map((item, index) => (
            <div key={index} className="summary-item">
              <div className="summary-question">{item.step}</div>
              <div className="summary-action">{item.action}</div>
            </div>
          ))}
        </div>
      )}
      
      <div className="summary-actions-buttons">
        <button className="btn btn-primary" onClick={downloadPDF}>📄 Download as PDF</button>
        <button 
          className={`btn ${isAssessmentComplete ? 'btn-success' : 'btn-secondary'}`} 
          onClick={() => setShowEmailInput(!showEmailInput)}
          disabled={!isAssessmentComplete}
          title={!isAssessmentComplete ? 'Complete all assessment steps to enable email functionality' : ''}
        >
          📧 Email Report
        </button>
        <button className="btn btn-secondary" onClick={onBack}>← Back to Exercises</button>
        <button className="btn btn-secondary" onClick={onRestart}>🔄 Start Over</button>
      </div>
      
      {showEmailInput && (
        <div className="email-input-container">
          {!isAssessmentComplete && (
            <div className="email-warning">
              ⚠️ Please complete all assessment steps before sending the report. The email will only contain completed information.
            </div>
          )}
          
          <form onSubmit={handleEmailSubmit}>
            <input 
              type="email" 
              className="email-input" 
              placeholder="Enter your email address" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
            <button type="submit" className="btn btn-success" disabled={isSending}>
              {isSending ? 'Sending...' : 'Send Report'}
            </button>
          </form>
          
          {emailError && (
            <div className="email-error">
              ❌ {emailError}
            </div>
          )}
          
          {emailSent && (
            <div className="email-success">
              ✅ Email sent successfully to {email}!
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SummaryReport;
